import { Vue } from 'vue-class-component';
import { Inject, Watch } from 'vue-property-decorator';
import type { VueI18n } from 'vue-i18n';
import { ReserveVehicleWidget } from '@/components/views/customize-deal/components/reserve-vehicle-widget';
import { VehicleInfoContainer } from '@/components/vehicle-info-container';
import { DealTypeSelectorContainer } from '@/components/deal-type-selector-container';
import { DealOptions } from '@/components/views/customize-deal/components/deal-options';
import { CashDownSlider } from '@/components/views/customize-deal/components/cash-down-slider';
import { Scrollable } from '@/components/scrollable';
import { TabSelector } from '@/components/tab-selector';
import { LengthSelector } from '@/components/views/customize-deal/components/length-selector';
import { IncentivesSelector } from '@/components/views/customize-deal/components/incentives-selector';
import { CompareDataObject, DealType, IncentiveRequest, PaymentTermsDetails, RebateSource } from '@/types/writeup';
import throttle from 'lodash/throttle';
import { MilesSelector } from '@/components/views/customize-deal/components/miles-selector';
import { Button, ButtonTypes } from '@/components/button';
import { Routes } from '@/router/routes';
import { WriteupModuleContext } from '@/store/writeup';
import { CoreModuleContext } from '@/store/core';
import { TradeInSlider } from '@/components/views/customize-deal/components/trade-in-slider';
import { ProtectionsAndOptionsWidget } from '@/components/views/customize-deal/components/protections-and-options-widget';
import { RebatesWidget } from '@/components/views/customize-deal/components/rebates-widget';
import { AccessoriesWidget } from '@/components/views/customize-deal/components/accessories-widget';
import { ADVERTISED_OFFERS, AMPLITUDE_LABELS } from '@/common/constants';
import { CreditScoreSlider } from '@/components/views/customize-deal/components/credit-score-slider';
import { CbcModuleContext } from '@/store/cbc';
import { StepManagerModuleContext } from '@/store/step-manager';
import { CreditAppLauncher } from '@/components/views/customize-deal/components/credit-app-launcher';
import { DealStatusesApi, DealStepsApi } from '@/types/deal-progress';
import { Sources } from '@/store/core/state';
import { CircleLoader } from '@/components/circle-loader';
import { SdEvents } from '@/types/sd-events';
import { TealiumPage, TealiumLink } from '@/types/tealium-events';
import { DealerDiscount } from '@/components/views/customize-deal/components/dealer-discount';
import { LengthType, Terms } from './types';
import styles from './styles.module.scss';

const THROTTLE_TIME_LIMIT = 350;

export class CustomizeDealContainer extends Vue {
	throttledHandleCashDownChange!: (value: Partial<PaymentTermsDetails>) => void;

	throttledHandleTradeInChange!: (value: number) => void;

	throttledHandleScoreChange!: (value: Partial<PaymentTermsDetails>) => void;

	throttledHandleTradeinEvent!: (value: number) => void;

	lengthTab = LengthType.Manual;

	paymentTermsDetails!: Partial<PaymentTermsDetails>;

	isIncentivesUpdateLoading = false;

	isCreditScoreChanged = false;

	async created() {
		if ((window as Window).utag) {
			this.triggerTealium(TealiumPage.CustomizeDeal);
		}

		if (this.coreModule.getters.initData) {
			await this.writeupModule.actions.createWriteup(this.coreModule.getters.initData);
		}

		this.setPaymentTermsDetails();

		this.throttledHandleCashDownChange = throttle(value => {
			this.writeupModule.actions.updateTerms({ ...value });
		}, THROTTLE_TIME_LIMIT, { leading: false });

		this.throttledHandleScoreChange = throttle(value => {
			this.writeupModule.actions.updateTerms({ ...value });
		}, THROTTLE_TIME_LIMIT, { leading: false });

		this.throttledHandleTradeInChange = throttle(value => {
			this.writeupModule.actions.updateTradeIn({ amount: +value, userValue: true, tradingIn: true });
			this.triggerEvent('SlideAnEstimatedValue');
			this.triggerTealium(TealiumPage.TradeInSlider);
		}, 500, { leading: false });

		const urlParams = new URLSearchParams(window.location.search);

		if (urlParams.get('origin') === Sources.Email && this.coreModule.getters.originSource === Sources.App) {
			this.coreModule.mutations.setOriginSource(Sources.Email);
			await this.coreModule.actions.navigateTo(Routes.DEAL_DETAILS.path);
		}

		if (this.isPpLoaded === true) {
			if (this.dealType === DealType.lease || this.dealType === DealType.loan) {
				this.writeupModule.actions.trackSdEvent(SdEvents.CalculateStart);
			}
			this.writeupModule.actions.unSetPpLoaded();
		}
	}

	@Inject() readonly writeupModule!: WriteupModuleContext;

	@Inject() readonly cbcModule!: CbcModuleContext;

	@Inject() readonly coreModule!: CoreModuleContext;

	@Inject() readonly stepManagerModule!: StepManagerModuleContext;

	@Inject() readonly i18n!: VueI18n;

	get isPpLoaded() { return this.writeupModule.getters.ppLoadedState; }

	get writeup() { return this.writeupModule.getters.writeupObject; }

	get terms() { return this.writeupModule.getters.termsObject; }

	get isWriteupUpdating() { return this.writeupModule.getters.isWriteupUpdating; }

	get isTermsUpdating() { return this.writeupModule.getters.isTermsUpdating; }

	get isDealTypeUpdating() { return this.writeupModule.getters.isDealTypeUpdating; }

	get isIncentivesSaving() { return this.writeupModule.getters.isIncentivesSaving; }

	get compareData() { return this.terms?.compareData; }

	get zip() { return this.writeup?.zipCode; }

	get dealType() { return this.writeupModule.getters.dealType; }

	get down() { return this.writeup && this.terms ? this.writeup.terms.down : 0; }

	get minDown() { return this.terms ? this.terms.down.bottom : 0; }

	get maxDown() { return this.terms ? this.terms.down.top : 1000; }

	get termLength() { return this.writeup?.terms.termLength; }

	get programMiles() { return this.writeup?.terms.programMiles; }

	get hideCashDown() { return this.dealType === DealType.cash || (this.terms && this.terms.down.bottom >= this.terms.down.top); }

	get showRebates() { return this.writeup?.dataAvailability.rebates && (this.rebates.length !== this.excludedRebates.length); }

	get showLengthTab() {
		return this.writeup?.dataAvailability.incentives
			&& ((this.coreModule.getters.websiteSettings?.incentives.advertisedOffersOptions === ADVERTISED_OFFERS.all)
			|| (this.dealType && this.dealType !== DealType.cash
			&& this.coreModule.getters.websiteSettings?.incentives.advertisedOffersOptions === ADVERTISED_OFFERS[this.dealType]));
	}

	get hideTermLength() { return this.dealType === DealType.cash; }

	get hideMiles() { return this.dealType !== DealType.lease || this.lengthTab === LengthType.Offers; }

	get isLoading() { return !this.writeup || !this.terms || this.isDealTypeUpdating; }

	get tradeIn() { return this.writeup && this.writeup.pricing && this.writeup.pricing.tradeinValue ? this.writeup.pricing.tradeinValue : 0; }

	get istradeInValueEstimated() {
		return !!this?.writeup?.tradeIn?.year;
	}

	get protectionsTotalPrice() {
		return this.writeup?.pricing.protections ? Math.ceil(this.writeup.pricing.protections.serviceContract + this.writeup.pricing.protections.other)
			: 0;
	}

	get accessoriesTotalPrice() {
		return this.writeup?.pricing.accessories ? this.writeup?.pricing.accessories : 0;
	}

	get isCashDownLoading() { return this.isLoading || this.isIncentivesSaving || this.isTermsUpdating.some(term => term !== Terms.CashDown); }

	get isLengthLoading() { return this.isLoading || this.isIncentivesSaving || this.isTermsUpdating.some(term => term !== Terms.Length); }

	get isMilesLoading() { return this.isLoading || this.isTermsUpdating.some(term => term !== Terms.Miles); }

	get isIncentivesLoading() { return this.isLoading; }

	get isLengthTabLoading() { return this.isLoading || !!this.isTermsUpdating.length || this.isIncentivesSaving; }

	get lastIncentivesRequest() { return this.writeupModule.getters.lastIncentiveRequest; }

	get rebates() { return this.writeupModule.getters.rebates; }

	get excludedRebates() {
		// Following Rebates we are not showing inside rebates widget
		return this.rebates.filter(rebate => (rebate.source === RebateSource.DealerDiscount)
			|| (Number(rebate.cashValue) >= 0 && rebate.source === RebateSource.PriceRule)
			|| (rebate.source === RebateSource.DealerPriceNegotiation));
	}

	get rebatesValue() {
		return this.rebates?.filter(rebate => rebate.selected
					&& ((Number(rebate.cashValue) < 0 && rebate.source === RebateSource.PriceRule) || rebate.source === RebateSource.Manufacturer))
			.reduce((prev, rebate) => prev + Math.abs(+rebate.cashValue), 0);
	}

	handleDealTypeChange(type: DealType) {
		this.writeupModule.actions.changeDealType(type);
	}

	get incentives() { return this.writeupModule.getters.incentives || undefined; }

	get selectedIncentives() { return this.writeup?.selectedIncentives; }

	get activeIncentive() {
		if (!this.incentives || !this.writeup?.selectedIncentives[0]) { return undefined; }

		const selectedIncentive = this.writeup?.selectedIncentives[0];
		const { apr, months, miles } = selectedIncentive.term;

		for (let i = 0; i < this.incentives.length; i += 1) {
			if (selectedIncentive.id === this.incentives[i].id
				&& apr === this.incentives[i].terms[0].apr
				&& months === this.incentives[i].terms[0].months
				&& miles === this.incentives[i].terms[0].miles) {
				return i;
			}
		}

		return undefined;
	}

	get creditScore() {
		return this.writeup?.terms.creditScore;
	}

	get isCreditScoreUpdated() {
		return this.cbcModule.getters.isCreditScoreUpdated;
	}

	get showCreditAppLauncher() {
		return this.cbcModule.getters.showCreditAppLauncher;
	}

	get showAccessory() {
		return this.writeup?.dataAvailability?.accessory;
	}

	get showIncentives() { return this.writeup?.dataAvailability.incentives; }

	get priceGating() { return this.coreModule.getters.websiteSettings.priceGating; }

	get priceAdjustments() { return this.writeup?.pricing.priceAdjustments; }

	get discountValue() {
		return this.rebates
			.filter(rebate => rebate.source === RebateSource.DealerDiscount && rebate.selected)
			.reduce((acc, rebate) => acc + +rebate.cashValue, 0);
	}

	get showDealerDiscount() {
		let showDealerDiscountWidget = false;

		if (this.priceAdjustments) {
			showDealerDiscountWidget = this.priceGating === 1 || this.priceGating === 2;
		}

		return showDealerDiscountWidget;
	}

	get dealerDiscountTitle() {
		return this.discountValue && this.discountValue < 0 ? this.i18n.t('dealerDiscount.unlockedTitle') : this.i18n.t('dealerDiscount.title');
	}

	getValueByType(type: DealType) {
		if (!this.writeup || type !== this.writeup.type || type !== this.dealType) { return undefined; }

		return Math.ceil(type === DealType.cash ? this.writeup.pricing.totalPrice : this.writeup.pricing.monthly);
	}

	handleCashDownChange(value: number) {
		if (value === this.down) { return; }
		this.writeupModule.mutations.setWriteupUpdating();

		this.paymentTermsDetails.down = value;
		this.throttledHandleCashDownChange(this.paymentTermsDetails);
	}

	handleLengthTabChange(tab: LengthType) {
		// We reset selected incentives if target tab is manual,
		// If the writeup has selected incentive, reset selected object
		if (tab === LengthType.Manual && this.selectedIncentives?.length) {
			this.writeupModule.actions.saveSelectedIncentive({});
		}

		if (tab === LengthType.Offers) {
			this.triggerEvent('ChooseFromOffers');
			if (this.incentives?.length) {
				this.writeupModule.actions.saveSelectedIncentive(this.lastIncentivesRequest
					|| {
						id: this.incentives[0].id,
						term: {
							apr: this.incentives[0].terms[0].apr,
							months: this.incentives[0].terms[0].months,
							miles: this.incentives[0].terms[0].miles,
						},
					});
			}
		}

		this.lengthTab = tab;
	}

	updateLengthTab() {
		if (this.showLengthTab
			&& (this.coreModule.getters.websiteSettings?.displayOptions.showAdvertisedOfferFirst || this.selectedIncentives?.length)) {
			this.lengthTab = LengthType.Offers;
		} else {
			this.lengthTab = LengthType.Manual;
		}
	}

	@Watch('dealType')
	updateDealType() {
		this.updateLengthTab();
	}

	@Watch('showIncentives')
	updateShowIncentives() {
		this.updateLengthTab();
	}

	@Watch('isWriteupUpdating')
	updateProgramMiles() {
		if (this.dealType === DealType.lease && this.paymentTermsDetails) {
			const miles = this.paymentTermsDetails.programMiles === null ? this.writeup?.terms.programMiles : this.paymentTermsDetails.programMiles;

			this.paymentTermsDetails.programMiles = miles;
		}
	}

	setPaymentTermsDetails() {
		this.paymentTermsDetails = {
			termLength: this.writeup?.terms.termLength,
			creditScore: this.writeup?.terms.creditScore,
			userApr: this.writeup?.terms.userApr,
			down: this.writeup?.terms.down,
		};

		if (this.dealType === DealType.lease) {
			this.paymentTermsDetails.programMiles = this.writeup?.terms.programMiles;
		}

		if (this.dealType === DealType.loan) {
			const apr = this.getApr(this.writeup?.terms.termLength);

			this.paymentTermsDetails.apr = apr;
		}

		if (this.dealType === DealType.lease && this.writeup?.terms.programMiles) {
			this.paymentTermsDetails.programMiles = this.writeup?.terms.programMiles;
		}
	}

	@Watch('compareData')
	updateTermApr(value: CompareDataObject[]) {
		const ifTermsExists = value[0].terms.filter(term => term.termLength === this.writeup?.terms.termLength);

		if (this.isCreditScoreChanged) {
			if (ifTermsExists.length) {
				this.handleTermLengthChange(String(this.writeup?.terms.termLength));
			} else {
				const latestTerm = value[0].terms[value[0].terms.length - 1].termLength;

				this.handleTermLengthChange(String(latestTerm));
			}
		}
	}

	getApr(termLength: number | undefined) {
		return this.compareData && this.compareData[0] && this.compareData[0].terms
			&& this.compareData[0].terms.filter(termItem => termItem.termLength === termLength)[0].apr;
	}

	handleTermLengthChange(length: string) {
		this.isCreditScoreChanged = false;
		const ifTermsExists = this.compareData && this.compareData[0].terms.filter(term => term.termLength === this.writeup?.terms.termLength);

		let apr: number | undefined;

		if (ifTermsExists?.length) {
			apr = this.getApr(+length);
			if (this.dealType !== DealType.lease) {
				this.paymentTermsDetails.apr = apr;
			}
			this.paymentTermsDetails.termLength = +length;
		} else {
			const latestTerm = this.compareData && this.compareData[0].terms[this.compareData[0].terms.length - 1].termLength;

			apr = this.getApr(latestTerm);
			if (this.dealType !== DealType.lease) {
				this.paymentTermsDetails.apr = apr;
			}
			this.paymentTermsDetails.termLength = latestTerm;
		}

		this.writeupModule.actions.updateTerms(this.paymentTermsDetails);
	}

	async handleIncentiveChange(incentive: IncentiveRequest) {
		await this.writeupModule.actions.saveSelectedIncentive(incentive);
	}

	async handleMilesChange(miles: string) {
		this.paymentTermsDetails.programMiles = +miles;

		await this.writeupModule.actions.updateTerms(this.paymentTermsDetails);
	}

	handleCreditScoreChange(value: number) {
		if (value === this.creditScore) { return; }
		this.writeupModule.mutations.setWriteupUpdating();
		if (this.paymentTermsDetails) this.paymentTermsDetails.creditScore = value;
		this.isCreditScoreChanged = true;
		this.throttledHandleScoreChange(this.paymentTermsDetails);
	}

	resetScoreUpdated() {
		this.cbcModule.mutations.unsetIsCreditScoreUpdated();
	}

	toggleCreditAppLauncher() {
		this.cbcModule.mutations.toggleCreditAppLauncher();
	}

	handletradeInChange(value: number) {
		if (value === this.tradeIn) { return; }
		this.writeupModule.mutations.setWriteupUpdating();
		this.throttledHandleTradeInChange(value);
	}

	async updateStepStatus() {
		this.triggerEvent('FinalReview');
		if (this.writeup?.writeupId) {
			await this.stepManagerModule.actions.updateDealProgress({
				writeupId: this.writeup.writeupId,
				step: DealStepsApi.CustomizeDeal,
				status: DealStatusesApi.Completed,
			});
		}
		this.writeupModule.actions.trackSdEvent(SdEvents.CalculateComplete);
	}

	triggerEvent(action: string) {
		const eventData = {
			Label: AMPLITUDE_LABELS.customizeDeal,
			Action: action,
			...this.$route.meta,
		};

		this.coreModule.actions.sendEvent(eventData);
	}

	triggerAnalyticsEvent() {
		this.triggerTealium(TealiumPage.CustomizeDeal, TealiumLink.ScheduleTestDrive);
		this.triggerSdEvent();
	}

	triggerSdEvent() {
		this.writeupModule.actions.trackSdEvent(SdEvents.CalculateComplete);
	}

	async triggerTealium(pageName: TealiumPage, linkName: TealiumLink | undefined = undefined) {
		await this.writeupModule.actions.updateTealiumDataLayer(
			{
				pageMapping: pageName,
				linkMapping: linkName,
			}
		);
	}

	get isCbcEnabled() {
		return this.coreModule.getters.websiteSettings.creditAppSource;
	}

	get lenderSocketEnabled() {
		return this.coreModule.getters.websiteSettings.lenderSocketEnabled;
	}

	get minCreditScore() {
		let min = 300;

		if (this.dealType === DealType.lease) {
			min = this.coreModule.getters.websiteSettings.paymentTerms.lease.minimumLeaseCredit;
		}
		if (this.dealType === DealType.loan) {
			min = this.coreModule.getters.websiteSettings.paymentTerms.finance.minimumFinanceCredit;
		}
		return min;
	}

	render() {
		return (
			<div class="precise-price-app__scroll-container">
				<VehicleInfoContainer />
				<div class={ styles.customizeDealContainerContent }>
					<Scrollable>
						<ReserveVehicleWidget/>
						<CircleLoader show={ this.isWriteupUpdating }/>
						<div class="precise-price-app__container">
							<div class={ styles.customizeDealContainerTitle }>{ this.i18n.t('customizeDeal.title')}</div>
						</div>
						<DealTypeSelectorContainer />
						<div class="precise-price-app__container">
							<DealOptions zip={ this.zip } path={ Routes.ZIPCODE.path }/>
							{ this.showDealerDiscount
							&& <DealerDiscount
								discountValue={ this.discountValue }
								title={ this.dealerDiscountTitle }
								loading={this.isLoading}
							/> }
							<CashDownSlider
								v-show={ !this.hideCashDown }
								onChange={ this.handleCashDownChange }
								value={ this.down }
								min={ this.minDown }
								max={ this.maxDown }
								step={ 100 }
								loading={ this.isCashDownLoading }
							/>
							<CreditScoreSlider
								isScoreUpdated={ this.isCreditScoreUpdated }
								v-show={ !this.hideCashDown } onChange={ this.handleCreditScoreChange }
								value={ this.creditScore } loading={ this.isLoading }
								onScoreReset={ this.resetScoreUpdated }
								showCreditAppLauncher={ this.toggleCreditAppLauncher }
								min={ this.minCreditScore }
								lenderSocketEnabled={ this.lenderSocketEnabled }
								isCbcEnabled={ this.isCbcEnabled }
							/>
							<CircleLoader show={ this.isIncentivesUpdateLoading }/>
							{ !this.hideTermLength && <>
								{ this.showLengthTab
									&& <TabSelector
										class={ styles.customizeDealContainerSelector }
										active={ this.lengthTab }
										loading={ this.isLengthTabLoading }
										onChange={ this.handleLengthTabChange }
										tabs={ [
											{ title: this.i18n.t('lengthSelector.configureDeal'), id: LengthType.Manual },
											{ title: this.i18n.t('lengthSelector.selectOffer'), id: LengthType.Offers }]}
									/>
								}
								{ this.lengthTab === LengthType.Manual
									&& <LengthSelector
										class={ styles.customizeDealContainerWidget }
										type={ this.dealType }
										loading={ this.isLengthLoading || this.isIncentivesUpdateLoading }
										compareData={ this.isDealTypeUpdating ? undefined : this.compareData }
										active={ this.isIncentivesSaving ? undefined : String(this.termLength) }
										onChange={ this.handleTermLengthChange }
									/>
								}
								{ this.lengthTab === LengthType.Offers
									&& <IncentivesSelector
										class={ styles.customizeDealContainerWidget }
										type={ this.dealType }
										loading={ this.isIncentivesLoading }
										active={ this.activeIncentive }
										onChange={ this.handleIncentiveChange }
										incentives={ this.isDealTypeUpdating ? undefined : this.incentives }
									/> }
							</> }
							{ !this.hideMiles && <MilesSelector
								class={ styles.customizeDealContainerWidget }
								loading={ this.isMilesLoading }
								compareData={ this.compareData }
								active={ this.isIncentivesSaving ? undefined : String(this.programMiles) }
								onChange={ this.handleMilesChange }
							/> }
							<TradeInSlider
								value={ this.tradeIn } min={ 0 } max={ 100000 } step={ 100 } onChange={ this.handletradeInChange }
								loading={ this.isLoading } disabled={ this.istradeInValueEstimated }
							/>
							{ this.showRebates && <RebatesWidget
								class={ styles.customizeDealContainerWidget }
								value={ this.rebatesValue }
								loading={ this.isLoading }
								path={ Routes.REBATES_SETTINGS.path } /> }
							<ProtectionsAndOptionsWidget
								class={ styles.customizeDealContainerWidget }
								value={ this.protectionsTotalPrice }
							/>
							<AccessoriesWidget
								v-show={ this.showAccessory }
								class={ styles.customizeDealContainerWidget }
								value={ this.accessoriesTotalPrice }
							/>
							<div style="padding: 20px 0 0 0;">
								<Button fullWidth onClick={ this.updateStepStatus } to={ Routes.DEAL_DETAILS.path }>{ this.i18n.t('customizeDeal.readyToBuy')}</Button>
							</div>
							<CreditAppLauncher
								active={ this.showCreditAppLauncher }
								softPullLink={ Routes.CREDIT_SCORE.path }
								creditApplicationLink={ Routes.CREDIT_APPLICATION.path }
								onHideOverlay={ this.toggleCreditAppLauncher }
								isCbcEnabled={ this.isCbcEnabled }
								lenderSocketEnabled={ this.lenderSocketEnabled }
							/>
							<div class= { styles.customizeDealContainerButton }>
								<Button
									fullWidth
									to={ Routes.DEAL_PROGRESS.path }
									onClick = { this.triggerSdEvent }
									type={ ButtonTypes.primary }
									inverted={ true }>
									{ this.i18n.t('dealProgress.dealProgressRoute') }
								</Button>
							</div>
							<Button
								class={ styles.customizeDealContainerScheduleTestDrive }
								fullWidth
								type={ ButtonTypes.primary }
								inverted={ true }
								to={ Routes.SCHEDULE_TEST_DRIVE.path }
								onClick = { this.triggerAnalyticsEvent }>
								{ this.i18n.t('customizeDeal.scheduleTestDrive') }
							</Button>
						</div>
					</Scrollable>
				</div>
			</div>
		);
	}
}
